/**
 * WEB SIDE of the native sign-in handoff.
 *
 * The native app opens this web app at `/auth?native=1&redirect=fahali://…`.
 * Once the user is signed in here — by whatever method the web already
 * supports, Google included — this module mints a one-time code and sends the
 * browser back to the app.
 *
 * Captured at MODULE-LOAD time for the reason `oauthCapture.ts` records: React
 * Navigation's linking resolver strips the query string when it maps the path,
 * so reading `window.location.search` inside a screen effect is too late.
 *
 * 🔴 The `redirect` target is VALIDATED, not trusted. It arrives in a URL, so
 * anyone can put anything there; an unchecked value would let a crafted link
 * bounce a freshly-minted sign-in code to a scheme we do not own. Only the
 * app's own scheme is accepted.
 */

/** The only callback this app will ever hand a code to. */
const ALLOWED_REDIRECT_PREFIX = "fahali://";

/**
 * 🔴 The capture must SURVIVE A PAGE RELOAD.
 *
 * Google sign-in on this page uses `signInWithRedirect` whenever a handoff is
 * pending (see `firebase.ts` for why the popup is not an option inside a
 * Chrome Custom Tab). A redirect navigates to Google and back, which destroys
 * every module-level variable on the page — including the one below.
 *
 * Firebase does return to the originating URL, so `?native=1&redirect=…`
 * would probably still be there on the way back. "Probably" is the problem:
 * that assumption is load-bearing for whether sign-in works at all, it is
 * invisible when it breaks (the page simply never returns to the app), and it
 * belongs to a library we do not control. Mirroring the capture into
 * `sessionStorage` makes the question moot instead of answering it.
 *
 * `sessionStorage` is scoped to this tab and origin, so it survives the trip
 * to accounts.google.com and back, and dies with the Custom Tab.
 */
const STORAGE_KEY = "fahali-native-handoff-v1";

let captured: { redirect: string } | null = null;

function readStored(): string | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage.getItem(STORAGE_KEY) : null;
  } catch {
    // Private mode / blocked storage. Not fatal: the query string is still
    // the primary source, and this is the backup.
    return null;
  }
}

try {
  if (typeof window !== "undefined") {
    const p = new URLSearchParams(window.location?.search || "");
    // The URL is the authority when it carries the request; storage only fills
    // in for the reload leg, when the query string may already be gone.
    const fromUrl = p.get("native") === "1" ? p.get("redirect") || "" : "";
    const redirect = fromUrl || readStored() || "";
    // Reject anything that is not our scheme — including `https://`, which
    // would silently exfiltrate the code to a web endpoint. Applied to the
    // STORED value too: storage is attacker-writable from any script on this
    // origin, so trusting it because we wrote it once would be a mistake.
    if (redirect.startsWith(ALLOWED_REDIRECT_PREFIX)) {
      captured = { redirect };
      try {
        sessionStorage.setItem(STORAGE_KEY, redirect);
      } catch {
        /* storage unavailable — the in-memory capture still works if no reload */
      }
    }
  }
} catch {
  captured = null;
}

/**
 * Forget the pending handoff.
 *
 * Called once a code has been minted. Without this the stored request would
 * outlive its use: a later ordinary visit to the web app in the same tab would
 * find a handoff still pending and try to bounce the user into the Android app
 * for a sign-in nobody asked for.
 */
export function clearNativeHandoff(): void {
  captured = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** The native handoff request from the initial page load, or null. */
export function getNativeHandoff(): { redirect: string } | null {
  return captured;
}

/**
 * Is this page load part of an in-flight native handoff?
 *
 * 🔴 Named separately from `getNativeHandoff()` because the ROUTER needs it,
 * and for the reason the router needs it.
 *
 * The hand-back runs inside `AuthScreen`, and `RootStackNavigator` mounts
 * `AuthScreen` only when `(!user || user.isDemo)`. On the way back from
 * Google, Firebase restores the session BEFORE any screen renders — so a real
 * user exists, the auth screen is skipped, the dashboard renders instead, and
 * the mint never runs. Measured 2026-08-31: `google_redirect_dispatched` →
 * `floor.first_render {connected:true}` with **no `auth.screen_viewed`
 * between them**, and `native_handoff_minted` at ZERO for its whole lifetime.
 * The user ends up signed into the browser while the app that opened it is
 * handed nothing.
 *
 * So the handoff is a third in-flight protocol that must complete regardless
 * of session state, exactly like `isOAuthFlow()` and `isExtensionConnectFlow()`
 * beside it. Signing in is not the end of this flow; handing the session back
 * is.
 *
 * Always false on native — `captured` is only ever set from a browser URL.
 */
export function isNativeHandoffFlow(): boolean {
  return captured !== null;
}

export function isAllowedRedirect(redirect: string): boolean {
  return typeof redirect === "string" && redirect.startsWith(ALLOWED_REDIRECT_PREFIX);
}

/**
 * Mint a one-time code for the signed-in user and return the browser to the
 * app. Called only after Firebase reports a real user on this page.
 *
 * Returns an error string on failure rather than throwing, because the caller
 * has to keep the user on a working web page when the hand-back fails — a
 * blank screen after a successful sign-in is the worst outcome available.
 */
export async function completeNativeHandoff(
  apiBase: string,
  idToken: string,
  redirect: string,
): Promise<string | null> {
  if (!isAllowedRedirect(redirect)) return "That sign-in link is not valid for this app.";
  try {
    const res = await fetch(`${apiBase.replace(/\/$/, "")}/api/auth/native-handoff/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
    if (!res.ok) return "Could not finish handing your sign-in back to the app.";
    const { code } = (await res.json()) as { code?: string };
    if (!code) return "The server returned no sign-in code.";
    // Consumed: drop the stored request BEFORE navigating, so a tab that stays
    // open on this origin does not try to hand off again on a later visit.
    clearNativeHandoff();
    // Hand back. The code is single-use and expires in ~2 minutes.
    window.location.href = `${redirect}?code=${encodeURIComponent(code)}`;
    return null;
  } catch {
    return "Could not reach Fahali to finish signing you in.";
  }
}
